import { useEffect } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";

/**
 * Cinematic "classified intelligence file" modal for a Signal card.
 * Props: card (object | null), onClose ()
 */
export const SignalModal = ({ card, onClose }) => {
  useEffect(() => {
    if (!card) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [card, onClose]);

  if (typeof document === "undefined") return null;

  // Rendered at the document root so a transformed/filtered ancestor
  // (e.g. the page-transition <motion.main>) cannot offset the fixed overlay.
  return createPortal(
    <AnimatePresence>
      {card && (
        <motion.div
          data-testid="signal-modal"
          className="fixed inset-0 z-[120] flex items-stretch justify-center sm:items-center sm:p-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          {/* backdrop */}
          <motion.div
            className="absolute inset-0 bg-black/75 backdrop-blur-md"
            onClick={onClose}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />

          {/* panel */}
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-label={card.title}
            initial={{ opacity: 0, scale: 0.96, filter: "blur(14px)" }}
            animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
            exit={{ opacity: 0, scale: 0.97, filter: "blur(10px)" }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="relative z-10 flex h-full w-full max-w-2xl flex-col overflow-hidden border border-white/10 bg-[#0B0D10]/90 shadow-[0_40px_120px_rgba(0,0,0,0.7)] backdrop-blur-2xl sm:h-auto sm:max-h-[88vh] sm:rounded-[1.4rem]"
          >
            {/* blurred oversized logo */}
            <img
              src="/triarch-logo.png"
              alt=""
              aria-hidden="true"
              className="pointer-events-none absolute -right-24 -top-24 h-[440px] w-[440px] opacity-[0.05] blur-[2px]"
            />
            {/* grid */}
            <div className="pointer-events-none absolute inset-0 triarch-grid opacity-25" />
            {/* gold sheen */}
            <div
              className="pointer-events-none absolute inset-x-0 top-0 h-44"
              style={{
                background:
                  "radial-gradient(600px 170px at 28% 0%, rgba(203,174,120,0.1), transparent 70%)",
              }}
            />
            {/* subtle muted-gold edge pulse (replaces scanning line) */}
            <motion.div
              className="pointer-events-none absolute inset-0 z-[1] sm:rounded-[1.4rem]"
              style={{ border: "1px solid rgba(201,174,120,0.06)" }}
              animate={{ opacity: [0.3, 0.85, 0.3] }}
              transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
            />

            {/* header */}
            <div className="relative border-b border-white/[0.08] px-6 py-5 sm:px-8 sm:py-6">
              <span className="font-mono-accent text-[10px] uppercase tracking-[0.3em] text-[#C9AE78]/85">
                {card.fileLabel}
              </span>
              <h3 className="mt-3 font-display text-2xl font-semibold tracking-[-0.01em] text-white sm:text-3xl">
                {card.title}
              </h3>
            </div>

            {/* body */}
            <div className="relative min-h-0 flex-1 overflow-y-auto px-6 py-6 sm:px-8 sm:py-7">
              <div className="space-y-5">
                {card.paragraphs.map((p, i) => (
                  <p
                    key={`p-${i}`}
                    className="text-sm leading-relaxed text-white/60"
                  >
                    {p}
                  </p>
                ))}

                {card.definition && (
                  <div className="border-l border-[#C9AE78]/40 pl-4">
                    <p className="font-display text-base leading-relaxed text-white/85">
                      {card.definition}
                    </p>
                  </div>
                )}

                {/* command-style stacked lines */}
                <div className="space-y-2 py-1">
                  {card.commands.map((c, i) => (
                    <div key={`c-${i}`} className="flex items-center gap-3">
                      <span className="font-mono-accent text-[11px] text-[#C9AE78]/75">
                        &rsaquo;
                      </span>
                      <span className="font-mono-accent text-xs uppercase tracking-[0.16em] text-white/85">
                        {c}
                      </span>
                    </div>
                  ))}
                </div>

                <div className="h-px w-full bg-gradient-to-r from-white/12 to-transparent" />

                {card.outro.map((p, i) => (
                  <p
                    key={`o-${i}`}
                    className="text-sm leading-relaxed text-white/55"
                  >
                    {p}
                  </p>
                ))}
              </div>
            </div>

            {/* footer */}
            <div className="relative flex items-center justify-between border-t border-white/[0.08] px-6 py-4 sm:px-8">
              <span className="font-mono-accent text-[10px] uppercase tracking-[0.25em] text-white/30">
                Clearance: Internal
              </span>
              <button
                onClick={onClose}
                data-testid="signal-modal-collapse"
                className="font-mono-accent text-[11px] uppercase tracking-[0.2em] text-white/60 transition-colors duration-300 hover:text-[#C9AE78]"
              >
                Collapse Signal
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
};
